import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import { AppText } from "@toothfairy/shared-ui";
import mermaid from "mermaid";
import zenuml from "@mermaid-js/mermaid-zenuml/dist/mermaid-zenuml.core.mjs";

// Predefined mermaid samples used to preview theme variables
const SAMPLES = [
  {
    id: "flowchart",
    label: "Flowchart",
    code: `graph TD
  A[Start] --> B{Decision}
  B -->|Yes| C[Action 1]
  B -->|No| D[Action 2]
  C --> E[End]
  D --> E`,
  },
  {
    id: "sequence",
    label: "Sequence",
    code: `sequenceDiagram
  participant Alice
  participant Bob
  Alice->>Bob: Hello Bob, how are you?
  Bob-->>Alice: I am good thanks!`,
  },
  {
    id: "pie",
    label: "Pie",
    code: `pie title Pets adopted by volunteers
  "Dogs" : 386
  "Cats" : 85
  "Rats" : 15`,
  },
  {
    id: "xychart",
    label: "XY Chart",
    code: `xychart-beta
  title "Sales Revenue"
  x-axis [Jan, Feb, Mar, Apr, May, Jun]
  y-axis "Revenue" 0 --> 11000
  bar [5000, 6000, 7500, 8200, 9800, 10500]
  line [5000, 6000, 7500, 8200, 9800, 10500]`,
  },
  {
    id: "quadrant",
    label: "Quadrant",
    code: `quadrantChart
  title Reach and influence
  x-axis Low Reach --> High Reach
  y-axis Low Influence --> High Influence
  quadrant-1 We should expand
  quadrant-2 Need to promote
  quadrant-3 Re-evaluate
  quadrant-4 May be improved
  Campaign A: [0.3, 0.6]
  Campaign B: [0.45, 0.23]
  Campaign C: [0.57, 0.69]
  Campaign D: [0.78, 0.34]`,
  },
];

const buildThemeVariables = (values, currentTheme, mode) => ({
  background: currentTheme?.theme?.backgroundColor,
  primaryColor:
    values?.primaryColor || currentTheme?.theme?.primary,
  primaryTextColor:
    values?.primaryTextColor || currentTheme?.theme?.white,
  primaryBorderColor:
    values?.primaryBorderColor || currentTheme?.theme?.primary,
  lineColor:
    values?.lineColor || currentTheme?.theme?.darkGrey,
  secondaryColor:
    values?.secondaryColor || currentTheme?.theme?.mediumGrey,
  tertiaryColor:
    values?.tertiaryColor || currentTheme?.theme?.lightGrey,
  fontFamily: "normalFont",
  fontSize: currentTheme?.theme?.small_font_size,
  primaryTextColor: currentTheme?.theme?.font_color,
  quadrant1Fill: values?.quadrant1Fill || currentTheme?.theme?.lightGrey,
  quadrant2Fill: values?.quadrant2Fill || currentTheme?.theme?.lightGrey,
  quadrant3Fill: values?.quadrant3Fill || currentTheme?.theme?.lightGrey,
  quadrant4Fill: values?.quadrant4Fill || currentTheme?.theme?.lightGrey,
  quadrant1TextFill: values?.quadrant1TextFill || currentTheme?.theme?.primary,
  quadrant2TextFill: values?.quadrant2TextFill || currentTheme?.theme?.primary,
  quadrant3TextFill: values?.quadrant3TextFill || currentTheme?.theme?.primary,
  quadrant4TextFill: values?.quadrant4TextFill || currentTheme?.theme?.primary,
  quadrantPointFill: values?.quadrantPointFill || currentTheme?.theme?.primary,
  quadrantPointTextFill: currentTheme?.theme?.font_color,
  quadrantXAxisTextFill: currentTheme?.theme?.font_color,
  quadrantYAxisTextFill: currentTheme?.theme?.font_color,
  quadrantInternalBorderStrokeFill: currentTheme?.theme?.darkGrey,
  quadrantExternalBorderStrokeFill: currentTheme?.theme?.darkGrey,
  quadrantAxisLineStrokeFill: currentTheme?.theme?.font_color,
  quadrantPointStrokeFill: currentTheme?.theme?.font_color,
  quadrantTitleFill: currentTheme?.theme?.font_color,
  // Pie chart colors
  pie1: values?.pie1 || currentTheme?.theme?.primary,
  pie2: values?.pie2 || currentTheme?.theme?.orange,
  pie3: values?.pie3 || currentTheme?.theme?.green,
  pie4: values?.pie4 || currentTheme?.theme?.mediumGrey,
  pie5: values?.pie5 || currentTheme?.theme?.darkGrey,
  pieTitleTextSize: "16px",
  pieTitleTextColor: currentTheme?.theme?.font_color,
  pieSectionTextSize: "13px",
  pieSectionTextColor: currentTheme?.theme?.font_color,
  pieLegendTextSize: "12px",
  pieLegendTextColor: currentTheme?.theme?.font_color,
  pieStrokeColor: currentTheme?.theme?.backgroundColor,
  pieStrokeWidth: "2px",
  pieOuterStrokeWidth: "0px",
  pieOpacity: "0.85",
  // XY Chart
  xyChart: {
    backgroundColor:
      values?.xyBackgroundColor || currentTheme?.theme?.transparent,
    titleColor:
      values?.titleColor || currentTheme?.theme?.font_color,
    xAxisLabelColor:
      values?.xAxisLabelColor || currentTheme?.theme?.font_color,
    yAxisLabelColor:
      values?.yAxisLabelColor || currentTheme?.theme?.font_color,
    yAxisTitleColor:
      values?.yAxisTitleColor || currentTheme?.theme?.font_color,
    xAxisTitleColor:
      values?.xAxisTitleColor || currentTheme?.theme?.font_color,
    yAxisLineColor:
      values?.yAxisLineColor || currentTheme?.theme?.font_color,
    xAxisLineColor:
      values?.xAxisLineColor || currentTheme?.theme?.font_color,
    yAxisTickColor:
      values?.yAxisTickColor || currentTheme?.theme?.font_color,
    xAxisTickColor:
      values?.xAxisTickColor || currentTheme?.theme?.font_color,
    plotColorPalette: `${
      values?.plotPrimaryColor || "#1db8c6"
    },${values?.plotSecondaryColor || "#E95E0B"},${
      values?.plotTertiaryColor || "#28c552"
    }, #f10b0b`,
  },
});

const MermaidChartPreview = ({ values }) => {
  const { t } = useTranslation();
  const { currentTheme, mode } = AppTheme.useTheme();
  const containerRef = useRef(null);
  const [activeSample, setActiveSample] = useState("flowchart");
  const [rendered, setRendered] = useState(false);
  const [error, setError] = useState(null);

  const sample = SAMPLES.find((s) => s.id === activeSample);

  useEffect(() => {
    const renderMermaid = async () => {
      if (!containerRef.current) return;
      try {
        setError(null);
        const themeVars = buildThemeVariables(values, currentTheme, mode);
        mermaid.initialize({
          startOnLoad: false,
          darkMode: mode === "dark",
          theme: "base",
          themeVariables: themeVars,
          flowchart: { useMaxWidth: true },
        });
        await mermaid.registerExternalDiagrams([zenuml]);
        const { svg } = await mermaid.render(
          `mermaid-preview-${activeSample}-${Date.now()}`,
          sample.code
        );
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
          setRendered(true);
        }
      } catch (e) {
        console.error("Mermaid preview render error:", e);
        setError(e.message || "Failed to render chart");
        if (containerRef.current) {
          containerRef.current.innerHTML = `<div style="color: ${currentTheme?.theme?.red}; padding: 20px; font-family: monospace; font-size: 12px; white-space: pre-wrap;">${e.message || "Render error"}</div>`;
        }
      }
    };
    renderMermaid();
  }, [activeSample, JSON.stringify(values), mode]);

  return (
    <View
      style={{
        width: Platform.OS === "web" ? 480 : "100%",
        marginTop: 20,
        alignSelf: "flex-start",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 6,
          marginBottom: 12,
        }}
      >
        {SAMPLES.map((s) => {
          const isActive = activeSample === s.id;
          return (
            <AppText
              key={s.id}
              isClickable
              onPress={() => setActiveSample(s.id)}
              style={{
                fontSize: 12,
                fontWeight: isActive ? "700" : "400",
                paddingVertical: 5,
                paddingHorizontal: 10,
                borderRadius: 6,
                backgroundColor: isActive
                  ? currentTheme?.theme?.primary
                  : currentTheme?.theme?.softBackgroundColor,
                color: isActive
                  ? currentTheme?.theme?.white
                  : currentTheme?.theme?.font_color,
                borderWidth: isActive ? 1 : 0,
                borderColor: currentTheme?.theme?.primary,
              }}
            >
              {s.label}
            </AppText>
          );
        })}
      </View>

      <View
        style={{
          borderWidth: 1,
          borderColor: currentTheme?.theme?.lightGrey,
          borderRadius: 10,
          padding: 16,
          minHeight: 320,
          backgroundColor: currentTheme?.theme?.backgroundColor,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {error && (
          <View style={{ marginBottom: 10 }}>
            <AppText
              style={{ fontSize: 12, color: currentTheme?.theme?.orange }}
            >
              {t("admin.charting.previewError", "Preview error")}: {error}
            </AppText>
          </View>
        )}
        <div
          ref={containerRef}
          style={{
            width: "100%",
            minHeight: 280,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        />
        {!rendered && !error && (
          <AppText
            style={{
              fontSize: 12,
              color: currentTheme?.theme?.mediumGrey,
              position: "absolute",
            }}
          >
            {t("admin.charting.loadingPreview", "Rendering preview...")}
          </AppText>
        )}
      </View>

      <AppText
        style={{
          fontSize: 11,
          color: currentTheme?.theme?.mediumGrey,
          marginTop: 8,
          textAlign: "center",
        }}
      >
        {t(
          "admin.charting.previewHint",
          "This preview updates live as you change colors above"
        )}
      </AppText>
    </View>
  );
};

export default MermaidChartPreview;
